import React, { useRef, useState, useEffect, FC } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'linaria/react';
import { COLORS, FONTS } from 'styles/constants';

import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';
import { useSendReaction } from 'graphQL/reactions/hooks';
import { useSendMessageMutation, useCreateChatMutation, ChatType } from 'constants/graphqlTypes';
import { formatMessageBeforeSending } from 'helpers/messagesMentions';
import useToast from 'helpers/useToast';
import useDisableScroll from 'helpers/useDisableScroll';

import { StyledToolTipWrap } from './styled';
import { useReactionContext } from '../hooks';

// Placeholder emoji list
const EMOJIS = ['👍', '❤️', '👏', '🔥', '😂'];

interface ReactionMenuProps {
  isOpen: boolean;
  anchorPoint: { x: number; y: number };
  onClose: () => void;
  onSend: () => void;
  onEmoji: (emoji: string) => void;
  onOpenReactionsModal?: () => void;
}

const REACTION_MENU_CLASSNAME = 'reaction-menu';

const MenuContainer = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: white;
  border-radius: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  min-width: 320px;
  max-width: 420px;
  transform: translateY(10px);
  opacity: 0;
  animation: slideInFadeIn 0.2s ease-out forwards;
  z-index: 10000000;

  @keyframes slideInFadeIn {
    0% {
      transform: translateY(10px);
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const EmojiRow = styled.ul`
  display: flex;
  gap: 8px;
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
  justify-content: flex-start;
`;

const EmojiButton = styled.button<{ index: number }>`
  align-items: center;
  background-color: ${COLORS.white[100]};
  border: 1px solid ${COLORS.white.off[100]};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 44px;
  width: 44px;
  user-select: none;
  position: relative;
  z-index: 200;
  font: 400 22px/130% ${FONTS.primary};
  transition: background 0.15s;
  transform: scale(0.45) rotate(90deg);
  opacity: 0;
  animation: emojiButtonAnimation 0.23s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  animation-delay: ${({ index }) => index * 0.1}s;

  &:hover,
  &:focus {
    background: #f5f5f5;
    outline: none;
  }

  @keyframes emojiButtonAnimation {
    0% {
      transform: scale(0.45) rotate(90deg);
      opacity: 0;
    }
    100% {
      transform: scale(1) rotate(0deg);
      opacity: 1;
    }
  }
`;

const PlusButton = styled(EmojiButton)`
  font-size: 2rem;
  font-weight: bold;
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  align-self: stretch;
  width: 100%;
`;

const StyledTextarea = styled.textarea<{ hasSendButton: boolean }>`
  display: flex;
  padding: 12px 20px;
  align-items: center;
  gap: 1px;
  flex: 1 0 0;
  width: ${({ hasSendButton }) => (hasSendButton ? 'calc(100% - 90px)' : '100%')};
  transition: width 0.23s cubic-bezier(0.33, 0, 0.31, 1);
  border: none;
  border-radius: 22px;
  background: var(--Off-white, #efeeea);
  font-size: 16px;
  font-family: ${FONTS.secondary};
  resize: none;
  min-height: 40px;
  max-height: 72px;
  overflow-y: auto;
  box-sizing: border-box;
  outline: none;
  transition: box-shadow 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const SendButton = styled.button<{ visible: boolean }>`
  background: #19190f;
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  width: ${({ visible }) => (visible ? '90px' : '0px')};
  padding: ${({ visible }) => (visible ? '12px 24px' : '12px 0')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: width 0.23s cubic-bezier(0.33, 0, 0.31, 1), opacity 0.15s cubic-bezier(0.33, 0, 0.31, 1), background 0.15s;
  &:hover,
  &:focus {
    background: #33331a;
    outline: none;
  }
  white-space: nowrap;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999999;
  background: transparent;
`;

const Positioner = styled.div<{ top: number; left: number }>`
  position: fixed;
  z-index: 10000000;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
`;

const ArrowContainer = styled.div<{ isBelow: boolean; arrowLeft: number; visible: boolean }>`
  position: absolute;
  top: ${({ isBelow }) => (isBelow ? '-12px' : 'auto')};
  bottom: ${({ isBelow }) => (isBelow ? 'auto' : '-12px')};
  left: ${({ arrowLeft }) => arrowLeft}px;
  width: 21px;
  height: 12px;
  pointer-events: none;
  z-index: 10000000;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
`;

const ArrowSVG = styled.svg<{ isBelow: boolean }>`
  display: block;
  transform: ${({ isBelow }) => (isBelow ? 'rotate(180deg)' : 'none')};
`;

// Removed unused styled components

const ReactionMenu: FC<ReactionMenuProps> = ({ isOpen, anchorPoint, onClose, onEmoji, onOpenReactionsModal }) => {
  const [message, setMessage] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isMenuBelow, setIsMenuBelow] = useState(false);
  const [showArrow, setShowArrow] = useState(false);

  const { cardId, pickerId, user } = useReactionContext();
  const [sendMessage] = useSendMessageMutation();
  const [createChat] = useCreateChatMutation();
  const { setToast } = useToast();

  // Move useSendReaction hooks outside the callback
  const sendThumbsUp = useSendReaction('👍');
  const sendHeart = useSendReaction('❤️');
  const sendClap = useSendReaction('👏');
  const sendFire = useSendReaction('🔥');
  const sendLaugh = useSendReaction('😂');

  // Typewriter effect for placeholder
  useEffect(() => {
    if (isOpen) {
      setPlaceholderText('');
      const fullText = 'Write a message';
      let currentIndex = 0;

      const timer = setTimeout(() => {
        const totalDuration = 250; // 0.25s total duration
        const totalCharacters = fullText.length;

        // Cubic-bezier(0.33, 0.00, 0.31, 1.00) timing function
        const cubicBezier = (t: number): number => {
          const p1 = 0.33;
          const p2 = 0.0;
          const p3 = 0.31;
          const p4 = 1.0;
          return (1 - t) ** 3 * p1 + 3 * (1 - t) ** 2 * t * p2 + 3 * (1 - t) * t ** 2 * p3 + t ** 3 * p4;
        };

        const startTime = Date.now();

        const typewriterInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / totalDuration, 1);
          const easedProgress = cubicBezier(progress);

          const targetIndex = Math.floor(easedProgress * totalCharacters);

          if (targetIndex > currentIndex && currentIndex < fullText.length) {
            setPlaceholderText(fullText.slice(0, targetIndex + 1));
            currentIndex = targetIndex;
          }

          if (progress >= 1) {
            clearInterval(typewriterInterval);
          }
        }, 16); // ~60fps for smooth animation

        return () => clearInterval(typewriterInterval);
      }, 100); // 0.1s delay after menu opens

      return (): void => clearTimeout(timer);
    }
    setPlaceholderText('');
    return undefined;
  }, [isOpen]);

  // Position menu relative to anchor point within viewport bounds
  useEffect(() => {
    if (!isOpen) return undefined;

    const estimatedMenuHeight = 112;
    const estimatedMenuWidth = 320;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // anchorPoint coordinates are viewport-relative (from getBoundingClientRect)
    // Always ensure the menu is positioned within the visible viewport
    let top: number;
    let left: number;
    let isBelow: boolean;

    // Try to position above the anchor point first
    const topPosition = anchorPoint.y - estimatedMenuHeight - 10.5 - 4;
    const bottomPosition = anchorPoint.y + 44;

    // Check if there's enough space above
    if (topPosition >= 8) {
      // Position above
      top = topPosition;
      isBelow = false;
    } else if (bottomPosition + estimatedMenuHeight <= viewportHeight - 8) {
      // Position below
      top = bottomPosition;
      isBelow = true;
    } else {
      // No space above or below, position at top of viewport
      top = 8;
      isBelow = false;
    }

    // Position menu so the triangle aligns with the anchor point (reaction button)
    left = anchorPoint.x - estimatedMenuWidth / 2;

    // Ensure menu stays within viewport bounds horizontally
    if (left < 8) left = 8;
    if (left + estimatedMenuWidth > viewportWidth - 8) {
      left = viewportWidth - estimatedMenuWidth - 8;
    }

    setMenuPos({ top, left });
    setIsMenuBelow(isBelow);

    // Show arrow after menu animation
    setShowArrow(false);
    setTimeout(() => setShowArrow(true), 250);
    return undefined;
  }, [isOpen, anchorPoint]);

  // Auto-expand textarea up to 72px
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
      const { scrollHeight } = textareaRef.current;
      const newHeight = Math.min(scrollHeight, 72);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [message]);

  // Close on click outside (robust, like old customEventHandler)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handle = (e: MouseEvent): void => {
      // Handle both HTMLElement and SVGElement
      let targetElement: Element | null = null;
      if (e.target instanceof HTMLElement) {
        targetElement = e.target.closest(`.${REACTION_MENU_CLASSNAME}`);
      } else if (e.target instanceof SVGElement) {
        // For SVG elements, traverse up to find the menu container
        let current = e.target as Element;
        while (current && current !== document.body) {
          if (current.classList && current.classList.contains(REACTION_MENU_CLASSNAME)) {
            targetElement = current;
            break;
          }
          current = current.parentElement || (current.parentNode as Element);
        }
      }

      if (targetElement) {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [isOpen, onClose]);

  // Close on scroll or resize (but ignore keyboard-related changes)
  useEffect(() => {
    if (!isOpen) return undefined;

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const initialHeight = window.innerHeight;

    const handleScrollOrResize = () => {
      // Ignore if keyboard is likely open (viewport height decreased significantly)
      if (initialHeight - window.innerHeight > 100) return;

      // Ignore if an input is focused
      const { activeElement } = document;
      if (activeElement?.tagName === 'TEXTAREA' || activeElement?.tagName === 'INPUT') return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(onClose, 100);
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize, true);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize, true);
      clearTimeout(scrollTimeout);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setShowArrow(false);
    }
    return undefined;
  }, [isOpen]);

  // Disable scroll when menu is open
  useDisableScroll(!isOpen);

  const isPositioned = !(menuPos.top === 0 && menuPos.left === 0);
  const arrowLeft = Math.max(16, Math.min(anchorPoint.x - menuPos.left - 10, 360 - 11)); // Move arrow 20px to the right and adjust bounds

  const handleSendMessage = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!message || !cardId) {
      setMessage('');
      onClose();
      return;
    }

    try {
      // If pickerId is not available, we need to find the card owner
      // Try to get the userId from the context user or pickerId
      const cardOwnerId = pickerId || user?.id;

      if (!cardOwnerId) {
        setMessage('');
        onClose();
        return;
      }

      // Create a chat with the card owner and send a text message
      const chatResult = await createChat({
        variables: { userIds: [cardOwnerId], chatType: ChatType.Single },
      });

      const chatId = chatResult?.data?.startChat?.chat?.id;
      if (chatId) {
        await sendMessage({
          variables: { id: chatId, text: formatMessageBeforeSending(message, []) },
        });

        // Show toast notification for sent message
        setToast({
          isToastOpen: true,
          toastItemStart: 'Message sent to ',
          toastItemName: user?.username || 'user',
          toastItemAltEnding: '',
          linesCount: 1,
        });
      }
    } catch (error) {
      // Error handling
    }
    setMessage('');
    onClose();
  };

  const emojiHandlers = [
    { emoji: '👍', handler: sendThumbsUp },
    { emoji: '❤️', handler: sendHeart },
    { emoji: '👏', handler: sendClap },
    { emoji: '🔥', handler: sendFire },
    { emoji: '😂', handler: sendLaugh },
  ];

  const handleEmojiClick = (handler: () => void, emoji: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
    onEmoji(emoji);
  };

  const handlePlusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenReactionsModal?.();
    onClose(); // Close the quick reaction menu when opening the full modal
  };

  return createPortal(
    <>
      <Overlay />
      <Positioner top={menuPos.top} left={menuPos.left} style={{ visibility: isPositioned ? 'visible' : 'hidden' }}>
        <StyledToolTipWrap>
          <ArrowContainer isBelow={isMenuBelow} arrowLeft={arrowLeft} visible={showArrow}>
            <ArrowSVG
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="12"
              viewBox="0 0 21 12"
              fill="white"
              isBelow={isMenuBelow}
            >
              <path d="M10.3923 12L0 0H20.7846L10.3923 12Z" fill="white" />
            </ArrowSVG>
          </ArrowContainer>
          <MenuContainer ref={menuRef} className={REACTION_MENU_CLASSNAME}>
            <EmojiRow>
              {emojiHandlers.map(({ emoji, handler }, index) => (
                <li key={emoji}>
                  <EmojiButton
                    aria-label={`React with ${emoji}`}
                    onClick={handleEmojiClick(handler, emoji)}
                    tabIndex={0}
                    index={index}
                  >
                    {emoji}
                  </EmojiButton>
                </li>
              ))}
              <li>
                <PlusButton aria-label="More reactions" tabIndex={0} index={EMOJIS.length} onClick={handlePlusClick}>
                  <PlusIcon />
                </PlusButton>
              </li>
            </EmojiRow>
            <InputRow>
              <StyledTextarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={1}
                placeholder={placeholderText}
                maxLength={200}
                aria-label="Write a message"
                hasSendButton={!!message}
                data-reaction-input
              />
              <SendButton
                visible={!!message}
                onClick={handleSendMessage}
                aria-label="Send message"
                tabIndex={message ? 0 : -1}
              >
                Send
              </SendButton>
            </InputRow>
          </MenuContainer>
        </StyledToolTipWrap>
      </Positioner>
    </>,
    document.body
  );
};

export default ReactionMenu;
